import { useState } from "react";
import { Badge, Button, Spinner, Text, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import type { MarketplaceEntitlement } from "../services/apiService";
import { openUpgradeLink } from "../teams/teamsClient";
import { config } from "../config";
import { useUiStyles, BoltIcon, CalendarIcon, DocumentIcon, IconTile, RocketIcon, ClockIcon, ShieldIcon, TeamsIcon, Footer } from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

interface SubscriptionGateProps {
  entitlement: MarketplaceEntitlement;
  insideTeams: boolean;
  onRefresh: () => Promise<void>;
}

const useStyles = makeStyles({
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "14px",
  },
  languagePicker: {
    display: "flex",
    gap: "2px",
    padding: "2px",
    borderRadius: "6px",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  languageButton: {
    minWidth: "36px",
  },
  leadCard: {
    padding: "22px",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "10px",
    marginTop: "18px",
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },
  featureCard: {
    minWidth: 0,
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    borderRadius: "7px",
  },
  featureTitle: {
    marginTop: "10px",
  },
  purchaseInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginTop: "18px",
    padding: "12px",
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border("1px", "solid", tokens.colorBrandBackground2),
    borderRadius: "7px",
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    display: "flex",
    gap: "10px",
    marginTop: "18px",
    flexWrap: "wrap",
  },
});

/**
 * Full-screen paywall shown whenever the signed-in tenant has no active
 * "paidapp-meetingangelo" Marketplace subscription. Purchases happen through
 * the Teams Store (or AppSource) — Teams has no public API for a 3rd-party
 * app to trigger the native purchase dialog directly — so this screen links
 * out to the right storefront and offers a manual "refresh" once the
 * purchase has gone through (our backend activates it automatically via the
 * Marketplace fulfillment webhook, usually within seconds).
 */
export function SubscriptionGate({ entitlement, insideTeams, onRefresh }: SubscriptionGateProps) {
  const s = useUiStyles();
  const styles = useStyles();
  const { language, setLanguage, t, tRaw } = useLanguage();
  const [refreshing, setRefreshing] = useState(false);
  const features = tRaw<Array<{ title: string; body: string }>>("subscriptionGate.features");
  const featureIcons = [<DocumentIcon />, <BoltIcon />, <CalendarIcon />, <TeamsIcon />];

  const teamsStoreUrl = `https://teams.microsoft.com/l/app/${config.teamsAppId}`;
  const buyUrl = entitlement.marketplaceListingUrl || teamsStoreUrl;

  const handleUpgrade = () => {
    void openUpgradeLink(buyUrl, insideTeams);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const pending = entitlement.status === "PendingFulfillmentStart";
  const suspended = entitlement.status === "Suspended";
  const cancelled = entitlement.status === "Unsubscribed";

  return (
    <main className={s.page}>
      <div className={styles.topBar}>
        <Badge appearance="tint" color="informative">{t("subscriptionGate.badge")}</Badge>
        <div className={styles.languagePicker} aria-label={t("subscriptionGate.languageLabel")}>
          <Button className={styles.languageButton} size="small" appearance={language === "tr" ? "primary" : "subtle"} onClick={() => setLanguage("tr")}>TR</Button>
          <Button className={styles.languageButton} size="small" appearance={language === "en" ? "primary" : "subtle"} onClick={() => setLanguage("en")}>EN</Button>
        </div>
      </div>

      <div className={mergeClasses(s.card, s.accentTop, styles.leadCard)}>
        <div className={s.row}>
          <IconTile variant="brand">
            <RocketIcon />
          </IconTile>
          <div className={s.grow}>
            <Text size={500} weight="semibold" block>
              {t("subscriptionGate.title")}
            </Text>
            <Text block style={{ margin: "6px 0 4px", color: "var(--colorNeutralForeground2)" }}>
              {t("subscriptionGate.subtitle")}
            </Text>
            <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
              {t("subscriptionGate.note")}
            </Text>
          </div>
        </div>

        <div className={styles.featureGrid}>
          {features.map((feature, index) => (
            <div className={styles.featureCard} key={feature.title}>
              <IconTile variant={index === 1 ? "success" : "brand"}>{featureIcons[index]}</IconTile>
              <Text className={styles.featureTitle} size={300} weight="semibold" block>{feature.title}</Text>
              <Text size={200} block style={{ marginTop: "4px", color: "var(--colorNeutralForeground3)" }}>{feature.body}</Text>
            </div>
          ))}
        </div>

        <div className={styles.purchaseInfo}>
          <ShieldIcon />
          <Text size={200} block>{t("subscriptionGate.purchaseInfo")}</Text>
        </div>

        {!entitlement.configured && (
          <div style={{ marginTop: "14px" }}>
            <Badge appearance="tint" color="informative" icon={<ClockIcon />}>
              {t("subscriptionGate.notConfiguredBadge")}
            </Badge>
            <Text size={200} block style={{ marginTop: "8px", color: "var(--colorNeutralForeground3)" }}>
              {t("subscriptionGate.notConfiguredBody")}
            </Text>
          </div>
        )}

        {entitlement.configured && pending && (
          <div style={{ marginTop: "14px" }}>
            <Badge appearance="tint" color="warning" icon={<ClockIcon />}>
              {t("subscriptionGate.pendingBadge")}
            </Badge>
            <Text size={200} block style={{ marginTop: "8px", color: "var(--colorNeutralForeground3)" }}>
              {t("subscriptionGate.pendingBody")}
            </Text>
          </div>
        )}

        {entitlement.configured && suspended && (
          <div style={{ marginTop: "14px" }}>
            <Badge appearance="tint" color="danger" icon={<ShieldIcon />}>
              {t("subscriptionGate.suspendedBadge")}
            </Badge>
            <Text size={200} block style={{ marginTop: "8px", color: "var(--colorNeutralForeground3)" }}>
              {t("subscriptionGate.suspendedBody")}
            </Text>
          </div>
        )}

        {entitlement.configured && cancelled && (
          <Text size={200} block style={{ marginTop: "14px", color: "var(--colorNeutralForeground3)" }}>
            {t("subscriptionGate.cancelled")}
          </Text>
        )}

        <div className={styles.actions}>
          {entitlement.configured && (
            <Button appearance="primary" onClick={handleUpgrade}>
              {t("subscriptionGate.buy")}
            </Button>
          )}
          <Button appearance="secondary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <Spinner size="tiny" /> : t("subscriptionGate.refresh")}
          </Button>
        </div>
      </div>

      <Footer>{t("subscriptionGate.footer", { offerId: entitlement.offerId })}</Footer>
    </main>
  );
}
